import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { Badge } from "@/components/common/DataTable";
import { leadService, userService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { LEAD_STATUS_COLOR } from "./LeadsPage";
import {
  ArrowLeft, Loader2, UserCheck, CheckCircle2, XCircle, Repeat, Gauge, Phone, Mail, Calendar, StickyNote,
} from "lucide-react";
import { formatCurrency } from "@/lib/currency";

const fmt = (v) => v == null ? "—" : formatCurrency(v);
const TABS = [["overview", "Overview"], ["activities", "Activities"], ["score", "Score"]];
const ACT_ICON = { Call: Phone, Email: Mail, Meeting: Calendar, Note: StickyNote, StatusChange: Repeat };

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [modal, setModal] = useState(null); // assign | qualify | convert

  const load = useCallback(async () => {
    setLoading(true);
    try { setLead(await leadService.get(id)); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!lead) return <p className="text-muted-foreground py-10 text-center">Lead not found</p>;

  const canEdit = hasPermission("LEAD.EDIT");
  const canAssign = hasPermission("LEAD.ASSIGN");
  const canConvert = hasPermission("LEAD.CONVERT");
  const converted = lead.status === "Converted";

  return (
    <div data-testid="lead-detail-page">
      <button onClick={() => navigate("/admin/leads")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3" data-testid="lead-back">
        <ArrowLeft className="h-4 w-4" /> Back to Leads
      </button>
      <PageHeader title={lead.fullName} subtitle={`${lead.leadCode} · ${lead.company || "—"}`}
        actions={
          <div className="flex items-center gap-2">
            {canAssign && !converted && <ActBtn testid="lead-assign-btn" onClick={() => setModal("assign")} icon={UserCheck}>Assign</ActBtn>}
            {canEdit && !converted && <ActBtn testid="lead-qualify-btn" onClick={() => setModal("qualify")} icon={CheckCircle2}>Qualify</ActBtn>}
            {canConvert && !converted && <ActBtn testid="lead-convert-btn" primary onClick={() => setModal("convert")} icon={Repeat}>Convert</ActBtn>}
          </div>
        }
      />

      <div className="flex items-center gap-2 mb-5">
        <Badge color={LEAD_STATUS_COLOR[lead.status] || "slate"}>{lead.status}</Badge>
        <Badge color={lead.rating === "Hot" ? "red" : lead.rating === "Cold" ? "slate" : "amber"}>{lead.rating}</Badge>
        <span className="text-sm text-muted-foreground">Score {lead.score ?? 0}</span>
        {converted && lead.convertedCustomerId && (
          <button onClick={() => navigate(`/admin/customers/${lead.convertedCustomerId}`)} className="text-sm text-primary hover:underline ml-2" data-testid="lead-view-customer">View Customer →</button>
        )}
      </div>

      <div className="flex gap-1 border-b border-border mb-5">
        {TABS.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`lead-tab-${key}`}>{label}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="bg-card rounded-xl border border-border p-6 grid sm:grid-cols-2 gap-x-8 gap-y-4" data-testid="lead-overview">
          <Row label="Email" value={lead.email} />
          <Row label="Mobile" value={lead.mobile} />
          <Row label="Title" value={lead.title} />
          <Row label="Estimated Value" value={fmt(lead.estimatedValue)} />
          <Row label="Status" value={lead.status} />
          <Row label="Assigned To" value={lead.assignedTo || "Unassigned"} />
          <div className="sm:col-span-2"><Row label="Description" value={lead.description} /></div>
          {lead.qualification && <div className="sm:col-span-2"><Row label="Qualification" value={`${lead.qualification.result} — ${lead.qualification.reason || "no reason"}`} /></div>}
        </div>
      )}
      {tab === "activities" && <ActivitiesTab leadId={id} canEdit={canEdit && !converted} reload={load} />}
      {tab === "score" && <ScoreTab leadId={id} canEdit={canEdit && !converted} score={lead.score} reload={load} />}

      {modal === "assign" && <AssignModal lead={lead} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal === "qualify" && <QualifyModal lead={lead} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal === "convert" && <ConvertModal lead={lead} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} navigate={navigate} />}
    </div>
  );
}

function ActBtn({ children, onClick, icon: Icon, primary, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors ${primary ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border border-border hover:bg-muted text-foreground"}`}>
      <Icon className="h-4 w-4" /> {children}
    </button>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-0.5">{label}</p>
      <p className="text-sm text-foreground">{value || "—"}</p>
    </div>
  );
}

function ActivitiesTab({ leadId, canEdit, reload }) {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("Call");
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(() => { leadService.activities(leadId).then((d) => setItems(d.items || [])).catch(() => {}); }, [leadId]);
  useEffect(() => { refresh(); }, [refresh]);

  const add = async () => {
    if (!subject && !body) return toast.error("Add a subject or note");
    setSaving(true);
    try { await leadService.addActivity(leadId, { type, subject, body }); setSubject(""); setBody(""); toast.success("Activity logged"); refresh(); reload(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="lead-activities">
      {canEdit && (
        <div className="bg-card rounded-xl border border-border p-4 mb-4 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <select value={type} onChange={(e) => setType(e.target.value)} className="ciq-input" data-testid="activity-type">
              {["Call", "Email", "Meeting", "Note"].map((t) => <option key={t}>{t}</option>)}
            </select>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="ciq-input col-span-2" data-testid="activity-subject" />
          </div>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Notes…" className="ciq-input" rows={2} data-testid="activity-body" />
          <div className="flex justify-end">
            <button onClick={add} disabled={saving} className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60" data-testid="activity-add">Log Activity</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No activities yet</p>}
        {items.map((a) => {
          const Icon = ACT_ICON[a.type] || StickyNote;
          return (
            <div key={a.id} className="bg-card rounded-lg border border-border p-3 flex gap-3" data-testid={`activity-${a.id}`}>
              <Icon className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{a.subject || a.type}</p>
                {a.body && <p className="text-sm text-muted-foreground">{a.body}</p>}
                <p className="text-xs text-muted-foreground mt-1">{a.authorName} · {a.type}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ScoreTab({ leadId, canEdit, score, reload }) {
  const [val, setVal] = useState(score ?? 0);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await leadService.setScore(leadId, { score: Number(val), factors: [] }); toast.success("Score updated"); reload(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-card rounded-xl border border-border p-6 max-w-md" data-testid="lead-score-tab">
      <div className="flex items-center gap-2 mb-4 text-foreground"><Gauge className="h-5 w-5 text-primary" /><h3 className="font-semibold">Lead Score</h3></div>
      <p className="text-sm text-muted-foreground mb-3">Score 0–100 sets the rating (≥70 Hot, ≥40 Warm, else Cold).</p>
      <input type="range" min="0" max="100" value={val} onChange={(e) => setVal(e.target.value)} className="w-full" disabled={!canEdit} data-testid="score-slider" />
      <p className="text-3xl font-bold text-foreground my-2">{val}</p>
      {canEdit && <button onClick={save} disabled={saving} className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60" data-testid="score-save">Save Score</button>}
    </div>
  );
}

function Modal({ title, children, onClose, footer }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="lead-action-modal">
        <h3 className="text-lg font-bold mb-4">{title}</h3>
        <div className="space-y-3.5">{children}</div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
          {footer}
        </div>
      </div>
    </div>
  );
}

function AssignModal({ lead, onClose, onDone }) {
  const [users, setUsers] = useState([]);
  const [uid, setUid] = useState(lead.assignedTo || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { userService.list({ tenantId: lead.tenantId, size: 200 }).then((d) => setUsers(d.items || [])).catch(() => {}); }, [lead.tenantId]);
  const go = async () => {
    if (!uid) return toast.error("Select a user");
    setSaving(true);
    try { await leadService.assign(lead.id, uid); toast.success("Lead assigned"); onDone(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Assign Lead" onClose={onClose}
      footer={<button onClick={go} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-60" data-testid="assign-confirm">Assign</button>}>
      <label className="block text-sm font-medium mb-1">Assign to</label>
      <select value={uid} onChange={(e) => setUid(e.target.value)} className="ciq-input" data-testid="assign-user">
        <option value="">Select user…</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
      </select>
    </Modal>
  );
}

function QualifyModal({ lead, onClose, onDone }) {
  const [qualified, setQualified] = useState(true);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const go = async () => {
    setSaving(true);
    try { await leadService.qualify(lead.id, { qualified, reason }); toast.success(qualified ? "Lead qualified" : "Lead disqualified"); onDone(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Qualify Lead" onClose={onClose}
      footer={<button onClick={go} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-60" data-testid="qualify-confirm">Save</button>}>
      <div className="flex gap-2">
        <button onClick={() => setQualified(true)} className={`flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold border ${qualified ? "bg-emerald-500/15 border-emerald-500 text-emerald-600" : "border-border"}`} data-testid="qualify-yes"><CheckCircle2 className="h-4 w-4" /> Qualified</button>
        <button onClick={() => setQualified(false)} className={`flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold border ${!qualified ? "bg-red-500/15 border-red-500 text-red-600" : "border-border"}`} data-testid="qualify-no"><XCircle className="h-4 w-4" /> Disqualified</button>
      </div>
      <label className="block text-sm font-medium mb-1">Reason</label>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} className="ciq-input" rows={2} data-testid="qualify-reason" />
    </Modal>
  );
}

function ConvertModal({ lead, onClose, onDone, navigate }) {
  const [createOpp, setCreateOpp] = useState(true);
  const [name, setName] = useState(`${lead.company || lead.fullName} — Opportunity`);
  const [amount, setAmount] = useState(lead.estimatedValue || "");
  const [saving, setSaving] = useState(false);
  const go = async () => {
    setSaving(true);
    try {
      const res = await leadService.convert(lead.id, {
        createOpportunity: createOpp, opportunityName: name,
        amount: amount === "" ? null : Number(amount),
      });
      toast.success("Lead converted to customer");
      onDone();
      if (res.customerId) navigate(`/admin/customers/${res.customerId}`);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Convert Lead → Customer" onClose={onClose}
      footer={<button onClick={go} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="convert-confirm">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Convert</button>}>
      <p className="text-sm text-muted-foreground">A customer record and primary contact will be created. The lead will be marked Converted.</p>
      <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={createOpp} onChange={(e) => setCreateOpp(e.target.checked)} data-testid="convert-create-opp" /> Also create an opportunity</label>
      {createOpp && (
        <>
          <div><label className="block text-sm font-medium mb-1">Opportunity name</label><input value={name} onChange={(e) => setName(e.target.value)} className="ciq-input" data-testid="convert-opp-name" /></div>
          <div><label className="block text-sm font-medium mb-1">Amount (INR)</label><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="ciq-input" data-testid="convert-opp-amount" /></div>
        </>
      )}
    </Modal>
  );
}
